<script setup lang="ts">
import { computed } from 'vue'
import { useRouter } from 'vue-router'
import BaseButton from '@/components/common/BaseButton.vue'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { activityPath, orderIdOf, scopeOf, useRealtimeStore } from '@/stores/realtime'
import type { Notification } from '@/types/api'
import {
  PhArrowCounterClockwise,
  PhArrowRight,
  PhBell,
  PhChatCircleDots,
  PhCheckCircle,
  PhCreditCard,
  PhPackage,
  PhScales,
  PhShieldCheck,
  PhStar,
  PhTruck,
  PhXCircle,
} from '@phosphor-icons/vue'

defineOptions({ name: 'NotificationBell' })

const maxShown = 5

const router = useRouter()
const realtime = useRealtimeStore()

const createdTime = (n: Notification) => new Date(n.createdAt ?? 0).getTime()

// Same set the badge counts: unread notifications about an order, newest first.
const recent = computed(() =>
  realtime.unread
    .filter(orderIdOf)
    .sort((a, b) => createdTime(b) - createdTime(a))
    .slice(0, maxShown),
)

function badgeText(count: number) {
  return count > 9 ? '9+' : String(count)
}

function formatTime(dateString?: string): string {
  if (!dateString) return ''
  const date = new Date(dateString)
  if (isNaN(date.getTime())) return ''
  const diffSec = Math.floor((Date.now() - date.getTime()) / 1000)
  if (diffSec < 60) return 'Just now'
  const diffMin = Math.floor(diffSec / 60)
  if (diffMin < 60) return `${diffMin}m ago`
  const diffHours = Math.floor(diffMin / 60)
  if (diffHours < 24) return `${diffHours}h ago`
  const diffDays = Math.floor(diffHours / 24)
  if (diffDays < 7) return `${diffDays}d ago`
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
}

interface NotificationMeta {
  icon: unknown
  iconClass: string
  bgClass: string
}

function getNotificationMetaByType(type: string): NotificationMeta {
  switch (type) {
    case 'order_created':
      return {
        icon: PhPackage,
        iconClass: 'text-accent-cyan',
        bgClass: 'bg-cyan-500/15 border-cyan-500/25',
      }
    case 'order_accepted':
      return {
        icon: PhCheckCircle,
        iconClass: 'text-accent-cyan',
        bgClass: 'bg-cyan-500/15 border-cyan-500/25',
      }
    case 'order_paid':
    case 'payment_received':
      return {
        icon: PhCreditCard,
        iconClass: 'text-accent-emerald',
        bgClass: 'bg-emerald-500/15 border-emerald-500/25',
      }
    case 'order_delivered':
      return {
        icon: PhTruck,
        iconClass: 'text-accent-purple',
        bgClass: 'bg-purple-500/15 border-purple-500/25',
      }
    case 'order_completed':
    case 'dispute_resolved':
    case 'listing_approved':
      return {
        icon: PhCheckCircle,
        iconClass: 'text-accent-emerald',
        bgClass: 'bg-emerald-500/15 border-emerald-500/25',
      }
    case 'order_cancelled':
    case 'listing_rejected':
      return {
        icon: PhXCircle,
        iconClass: 'text-accent-red',
        bgClass: 'bg-red-500/15 border-red-500/25',
      }
    case 'order_disputed':
      return {
        icon: PhScales,
        iconClass: 'text-accent-amber',
        bgClass: 'bg-amber-500/15 border-amber-500/25',
      }
    case 'payment_refunded':
      return {
        icon: PhArrowCounterClockwise,
        iconClass: 'text-accent-amber',
        bgClass: 'bg-amber-500/15 border-amber-500/25',
      }
    case 'new_message':
      return {
        icon: PhChatCircleDots,
        iconClass: 'text-accent-cyan',
        bgClass: 'bg-cyan-500/15 border-cyan-500/25',
      }
    case 'review_received':
      return {
        icon: PhStar,
        iconClass: 'text-accent-amber',
        bgClass: 'bg-amber-500/15 border-amber-500/25',
      }
    case 'kyc_status_changed':
      return {
        icon: PhShieldCheck,
        iconClass: 'text-accent-purple',
        bgClass: 'bg-purple-500/15 border-purple-500/25',
      }
    case 'system':
    default:
      return {
        icon: PhBell,
        iconClass: 'text-accent-cyan',
        bgClass: 'bg-cyan-500/15 border-cyan-500/25',
      }
  }
}

function resolveNotificationMeta(notification: Notification): NotificationMeta {
  const type = notification.type || ''
  if (type && type !== 'system') {
    return getNotificationMetaByType(type)
  }
  const title = (notification.title || '').toLowerCase()
  if (title.includes('cancel')) return getNotificationMetaByType('order_cancelled')
  if (title.includes('paid') || title.includes('payment')) return getNotificationMetaByType('order_paid')
  if (title.includes('message')) return getNotificationMetaByType('new_message')
  if (title.includes('order')) return getNotificationMetaByType('order_created')
  if (title.includes('dispute')) return getNotificationMetaByType('order_disputed')
  if (title.includes('deliver')) return getNotificationMetaByType('order_delivered')
  if (title.includes('complet')) return getNotificationMetaByType('order_completed')
  return getNotificationMetaByType('system')
}

// Opening the target page marks it read, which drops it from this list.
function open(notification: Notification) {
  void router.push(activityPath(orderIdOf(notification)!, scopeOf(notification)))
}

function openInbox() {
  void router.push('/inbox/')
}
</script>

<template>
  <DropdownMenu>
    <DropdownMenuTrigger as-child>
      <BaseButton
        variant="ghost"
        size="sm"
        :aria-label="realtime.unreadCount ? `Notifications (${realtime.unreadCount} unread)` : 'Notifications'"
      >
        <span class="relative">
          <PhBell class="h-5 w-5" />
          <span
            v-if="realtime.unreadCount"
            class="absolute -right-1.5 -top-1.5 h-4 min-w-4 rounded-full bg-cta px-1 text-center text-[10px] font-bold leading-4 text-cta-foreground ring-2 ring-canvas"
          >
            {{ badgeText(realtime.unreadCount) }}
          </span>
        </span>
      </BaseButton>
    </DropdownMenuTrigger>

    <DropdownMenuContent
      align="end"
      class="w-[380px] sm:w-[400px] max-w-[calc(100vw-2rem)] rounded-2xl border border-line bg-surface/95 backdrop-blur-md p-2 shadow-2xl shadow-black/40"
    >
      <div class="flex items-center justify-between px-2.5 py-1.5">
        <div class="flex items-center gap-2">
          <span class="text-sm font-semibold text-ink">Notifications</span>
          <span
            v-if="realtime.unreadCount"
            class="rounded-full bg-cyan-500/10 px-2 py-0.5 text-[11px] font-semibold text-accent-cyan border border-cyan-500/20"
          >
            {{ realtime.unreadCount }} new
          </span>
        </div>
        <button
          v-if="recent.length > 0"
          type="button"
          class="cursor-pointer text-xs font-medium text-ink-5 transition-colors hover:text-accent-cyan"
          @click.stop="openInbox"
        >
          View inbox
        </button>
      </div>

      <DropdownMenuSeparator class="my-1.5 bg-line" />

      <div v-if="recent.length === 0" class="flex flex-col items-center justify-center py-7 px-4 text-center">
        <div class="mb-2.5 flex h-10 w-10 items-center justify-center rounded-2xl border border-line bg-surface-2 text-accent-cyan">
          <PhCheckCircle class="h-5 w-5" weight="bold" />
        </div>
        <p class="text-sm font-semibold text-ink">You're all caught up.</p>
        <p class="mt-0.5 text-xs text-ink-5">No unread order notifications</p>
      </div>

      <div v-else class="space-y-1">
        <DropdownMenuItem
          v-for="notification in recent"
          :key="notification.id"
          class="group flex cursor-pointer items-start gap-3 rounded-xl p-2.5 transition-colors focus:bg-surface-2 hover:bg-surface-2/70 focus:text-ink select-none"
          @click="open(notification)"
        >
          <!-- Category icon -->
          <div
            :class="[
              'flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border transition-transform group-hover:scale-105',
              resolveNotificationMeta(notification).bgClass,
            ]"
          >
            <component
              :is="resolveNotificationMeta(notification).icon"
              :class="['h-4.5 w-4.5', resolveNotificationMeta(notification).iconClass]"
              weight="bold"
            />
          </div>

          <!-- Notification text -->
          <div class="min-w-0 flex-1">
            <div class="flex items-baseline justify-between gap-1.5">
              <span class="truncate text-sm font-semibold text-ink leading-tight">
                {{ notification.title }}
              </span>
              <span
                v-if="notification.createdAt"
                class="shrink-0 text-[11px] text-ink-5 font-normal"
              >
                {{ formatTime(notification.createdAt) }}
              </span>
            </div>
            <p class="mt-0.5 line-clamp-2 text-xs leading-relaxed text-ink-5 transition-colors group-hover:text-ink-4">
              {{ notification.body }}
            </p>
          </div>

          <!-- Unread indicator dot -->
          <span
            v-if="!notification.readAt"
            class="mt-1 h-1.5 w-1.5 shrink-0 rounded-full bg-cta"
            aria-hidden="true"
          />
        </DropdownMenuItem>
      </div>

      <DropdownMenuSeparator class="my-1.5 bg-line" />
      <DropdownMenuItem
        class="flex cursor-pointer items-center justify-center gap-1.5 rounded-xl py-2 text-xs font-semibold text-accent-cyan transition-colors hover:bg-surface-2/70 hover:text-accent-cyan-soft focus:bg-surface-2 focus:text-accent-cyan"
        @click="openInbox"
      >
        <span>Open Inbox</span>
        <PhArrowRight class="h-3.5 w-3.5" weight="bold" />
      </DropdownMenuItem>
    </DropdownMenuContent>
  </DropdownMenu>
</template>
